import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

const DEFAULT_WIDTH = 448
const MIN_WIDTH = 320
const MAX_WIDTH = 960

export const SAGE_CHAT_DRAWER_WIDTH_KEY = 'sageChatDrawerWidth'
export const NOTES_DRAWER_WIDTH_KEY = 'notesDrawerWidth'

function clampDrawerWidth(width: number): number {
  const max = Math.min(MAX_WIDTH, typeof window !== 'undefined' ? window.innerWidth - 24 : MAX_WIDTH)
  return Math.max(MIN_WIDTH, Math.min(max, Math.round(width)))
}

function readStoredWidth(storageKey: string, defaultWidth: number): number {
  try {
    const raw = localStorage.getItem(storageKey)
    const parsed = raw != null ? Number(raw) : NaN
    if (Number.isFinite(parsed)) return clampDrawerWidth(parsed)
  } catch {
    // ignore
  }
  return defaultWidth
}

/**
 * Right-edge drawer width with drag-to-resize and independent localStorage persistence.
 */
export function useResizableDrawerWidth(
  storageKey: string,
  defaultWidth: number = DEFAULT_WIDTH
): {
  width: number
  onResizePointerDown: (event: ReactPointerEvent) => void
} {
  const [width, setWidth] = useState(defaultWidth)
  const resizingRef = useRef(false)

  useEffect(() => {
    setWidth(readStoredWidth(storageKey, defaultWidth))
  }, [storageKey, defaultWidth])

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!resizingRef.current) return
      setWidth(clampDrawerWidth(window.innerWidth - event.clientX))
    }
    const onUp = () => {
      if (!resizingRef.current) return
      resizingRef.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setWidth((current) => {
        const clamped = clampDrawerWidth(current)
        try {
          localStorage.setItem(storageKey, String(clamped))
        } catch {
          // ignore
        }
        return clamped
      })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [storageKey])

  const onResizePointerDown = useCallback((event: ReactPointerEvent) => {
    event.preventDefault()
    resizingRef.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  return { width, onResizePointerDown }
}
